import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Box } from '../../layout/Box'
import Input from '../../layout/Input'
import { SubmitButton } from '../../../styles/components/buttons'
import { Loading } from '../../data/Loading'
import { ApplicationState } from '../../../store'
import { registerUser } from '../../../api/auth'
import { GenderSelect } from '../../data/GenderSelect'
import { DateSelect } from '../../data/DateSelect'
import { Gender } from '../../../store/user/types'
import { getBirthdayIsoString } from '../../../utils/date'
import { Footer } from '../../layout/Footer'
import { ErrorMessage } from '../../data/ErrorMessage'
import { PrivacyPolicyCheckbox } from '../../data/PrivacyPolicyCheckbox'
import { Typography } from '../../layout/Typography'
import { useLoadSalonData } from '../../../hooks/useLoadSalonData'
import { RegisterResponseType } from '../../../api/auth/register'
import { checkIfPhoneIsValid } from '../../../utils/validations/phone'
import { PhoneInput } from '../../data/PhoneInput'

const RegisterComponent: React.FC = () => {
  const [firstName, setFirstName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')
  const [phone, setPhone] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [passwordConfirm, setPasswordConfirm] = useState<string>('')
  const [gender, setGender] = useState<Gender>()
  const [birthdayDay, setBirthdayDay] = useState<string | null>(null)
  const [birthdayMonth, setBirthdayMonth] = useState<string | null>(null)
  const [birthdayYear, setBirthdayYear] = useState<string | null>(null)
  const [newsletter, setNewsletter] = useState<boolean>(true)
  const [privacyPolicy, setPrivacyPolicy] = useState<boolean>(false)

  const [loading, setLoading] = useState<boolean>(false)
  const [submittedMessage, setSubmittedMessage] = useState<string>('')
  const [errors, setErrors] = useState<string>('')

  useLoadSalonData({})

  const salonGuid = useSelector((state: ApplicationState) => state.salon.data?.guid)

  if (!salonGuid) return null

  const isFormValid = () => {
    if (!privacyPolicy) {
      setErrors('Bitte stimmen Sie der Datenschutzerklärung zu.')
      return false
    }

    if (!firstName) {
      setErrors('Bitte geben Sie Ihren Vornamen ein.')
      return false
    }

    if (!lastName) {
      setErrors('Bitte geben Sie Ihren Nachnamen ein.')
      return false
    }

    if (!email) {
      setErrors('Bitte geben Sie Ihre E-Mail-Adresse ein.')
      return false
    }

    if (!checkIfPhoneIsValid(phone)) {
      setErrors('Bitte gib eine gültige Telefonnummer ein.')
      return false
    }

    if (!password) {
      setErrors('Bitte geben Sie Ihr Passwort ein.')
      return false
    }

    if (password !== passwordConfirm) {
      setErrors('Die Passwörter stimmen nicht überein.')
      return false
    }

    return true
  }

  const resetForm = () => {
    setFirstName('')
    setLastName('')
    setPhone('')
    setEmail('')
    setPassword('')
    setPasswordConfirm('')
    setGender(undefined)
    setBirthdayDay(null)
    setBirthdayMonth(null)
    setBirthdayYear(null)
    setNewsletter(false)
    setPrivacyPolicy(false)

    setErrors('')
  }

  const submitRegister = async () => {
    if (!isFormValid()) return

    setLoading(true)
    const response = await registerUser({
      firstName,
      lastName,
      email,
      phone,
      password,
      gender: gender || 1,
      salonGuid,
      birthday: getBirthdayIsoString(birthdayDay || '1', birthdayMonth || '0', birthdayYear || '1970'),
      newsletter
    })

    setLoading(false)
    if (!response) {
      setErrors('No server response')
      return
    }

    if ('status' in response && response.status === RegisterResponseType.Exists) {
      setErrors(response.message)
      return
    }

    resetForm()
    setErrors('')
    setSubmittedMessage('Du hast dich erfolgreich registriert. Prüfe deine E-Mails, um deine E-Mail-Adresse zu bestätigen.')
  }

  const togglePrivacyPolicy = () => {
    setPrivacyPolicy(!privacyPolicy)
  }

  const toggleNewsletter = () => {
    setNewsletter(!newsletter)
  }

  return (
    <Box>
      {loading && <Loading />}

      {!submittedMessage && (
        <Box margin="1rem 0 0">
          <Input name="firstName" label="Vorname" value={firstName} onChange={setFirstName} />
          <Input name="lastName" label="Nachname" value={lastName} onChange={setLastName} />
          <Input name="email" label="Email" value={email} onChange={setEmail} />
          <Input name="password" type="password" label="Passwort" value={password} onChange={setPassword} />
          <Input name="passwordConfirm" type="password" label="Passwort bestätigen" value={passwordConfirm} onChange={setPasswordConfirm} />
          <PhoneInput phone={phone} setPhone={setPhone} />
          <GenderSelect onChange={setGender} />
          <DateSelect onDayChange={setBirthdayDay} onMonthChange={setBirthdayMonth} onYearChange={setBirthdayYear} />
          <Input name="newsletter" label="Newsletter anmelden" type="checkbox" value={newsletter} onChange={toggleNewsletter} />
          <PrivacyPolicyCheckbox value={privacyPolicy} onChange={togglePrivacyPolicy} />
        </Box>
      )}

      {errors && (
        <Box margin="0 0 1rem">
          <ErrorMessage>{errors}</ErrorMessage>
        </Box>
      )}

      {submittedMessage && (
        <Box margin="1.5rem 0">
          <Typography variant="bold">{submittedMessage}</Typography>
        </Box>
      )}

      {!submittedMessage && (
        <SubmitButton disabled={loading} onClick={submitRegister}>
          Register
        </SubmitButton>
      )}

      <Footer />
    </Box>
  )
}

export default RegisterComponent
